import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "agent",
  title: "Using the Mavori agent",
  navLabel: "Using the agent",
  description:
    "Use Mavori chat for grounded research, follow-up analysis, portfolio context, and work that continues across product surfaces.",
  section: "agent-research",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["use Mavori agent", "Mavori chat", "Mavori research agent"],
  related: ["markets", "data", "skills-and-files", "automations"],
  blocks: [
    {
      type: "lead",
      text: "The Mavori agent is the main place to ask, investigate, compare, and act. It combines the conversation with governed market data, the accounts you select, reusable skills, and durable work saved elsewhere in Mavori.",
    },

    { type: "h2", text: "Ask for evidence, not a verdict" },
    {
      type: "p",
      text: "A useful request names the subject, decision, time horizon, and comparison. Ask for the figures and events that support the answer, their dates, and what would change the conclusion. The agent can move between company data, filings, prices, options, news, macro context, and portfolio information when those sources are relevant.",
    },
    {
      type: "p",
      text: "Continue in the same conversation to challenge a claim, narrow the scope, or compare another scenario. Start a new chat when the objective changes materially so the title and history remain easy to scan.",
    },

    { type: "h2", text: "Inspect the work behind an answer" },
    {
      type: "p",
      text: "Answers can include activity details and a panel with the research or artifact produced during the turn. Open those details when a source, date, calculation, or coverage gap affects your decision. Mavori marks partial and missing inputs explicitly; see [Data coverage and freshness](/docs/data) for how to read them.",
    },

    { type: "h2", text: "Choose portfolio context explicitly" },
    {
      type: "p",
      text: "Use the Accounts control in the composer when the question depends on positions, cash, buying power, or account-specific limits. The selected accounts become context for that conversation. Research that does not depend on your holdings can stay account-free.",
    },

    { type: "h2", text: "Carry useful work forward" },
    {
      type: "ul",
      items: [
        "Save substantial reports and working material in **Files**.",
        "Use **Skills** when you want a repeatable specialist method or analysis lens.",
        "Keep standing preferences in **Rules & Memory** and trade reasoning in the **Journal**.",
        "Turn repeatable work into a scheduled task or alert when it should run later.",
      ],
    },
    {
      type: "p",
      text: "[Skills and files](/docs/skills-and-files) explains the durable artifacts around chat, while [Scheduled tasks and alerts](/docs/automations) covers work that runs when you are away.",
    },

    { type: "h2", text: "Review consequential actions" },
    {
      type: "p",
      text: "Research and drafting can proceed without placing a trade. A broker order or strategy deployment is shown as a specific action for review, with the destination account and relevant details. Read [Risk controls, approvals, and orders](/docs/trading) before enabling live trading.",
    },
  ],
};
